import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CONTRACT_ADDRESSES } from "@/config/contracts";
import { useState } from "react";
import { MintModal } from "./MintModal";
import { MINT_START_DATE, CHAIN_AVAILABILITY } from "@/config/mint";
import { CountdownTimer } from "./CountdownTimer";
import { useMintedQuantities } from "@/hooks/useMintedQuantities";

interface ChainSelectionProps {
  selectedChain: string | null;
  onChainSelect: (chainId: string) => void;
  isPastMintDate: boolean;
}

export function ChainSelection({ selectedChain, onChainSelect, isPastMintDate }: ChainSelectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mintingChain, setMintingChain] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const { quantities, loading } = useMintedQuantities();

  const CHIA_COLLECTION_URL = "https://mintgarden.io/collections/col1etvtg9y9xj0tq57nz25grk5d4la5j2v85zyff0j5hxpn5kt7zdzst8wmvc";

  const chainDetails = [
    {
      id: "base",
      name: "Base",
      logo: `${import.meta.env.BASE_URL}chain-logos/base.png`,
      price: "0.0013 ETH",
      tps: "~3,000",
      finality: "~3 minutes",
      supply: "121",
      minted: loading ? "..." : quantities.base.toString(),
      description: "Built by Coinbase, this blockchain is all about \"base-level security\"—but Altcoin Addict sees it as just another playground for degen adventures. It's simple, reliable, and maybe the only place The Reaper won't immediately show up. (But no promises!)",
      walletLink: "https://metamask.io/download/",
      walletName: "MetaMask"
    },
    {
      id: "pulsechain",
      name: "PulseChain",
      logo: `${import.meta.env.BASE_URL}chain-logos/pls.png`,
      price: "100,000 PLS",
      tps: "~2,500",
      finality: "~3 seconds",
      supply: "555",
      minted: loading ? "..." : quantities.pulsechain.toString(),
      description: "Known as the wild, high-speed highway of blockchains, Pulsechain is fast, cheap, and a bit like Luxury Lord's Lambo: flashy but might break down. Perfect for Token Trickster's quick-launch tokens and exit strategies!",
      walletLink: "https://metamask.io/download/",
      walletName: "MetaMask"
    },
    {
      id: "chia",
      name: "Chia",
      logo: `${import.meta.env.BASE_URL}chain-logos/chia.png`,
      price: "0.2 XCH",
      tps: "~20",
      finality: "~5 minutes",
      supply: "9324",
      minted: loading ? "..." : quantities.chia.toString(),
      description: "The eco-friendly blockchain that's greener than Luxury Lord's envy—and surprisingly reliable! With its sustainable farming approach, Chia claims to be the top pick for degens who want to save the planet while losing their money. It's perfect for Algorithm Assassin's schemes and Token Trickster's greenwashed moonshots.",
      collectionId: CHIA_COLLECTION_URL,
      walletLink: "https://goby.app/",
      walletName: "Goby Wallet"
    },
  ];

  const handleChainSelect = (chainId: string) => {
    if (!isPastMintDate || !CHAIN_AVAILABILITY[chainId as keyof typeof CHAIN_AVAILABILITY]) return;
    
    if (chainId === 'chia') {
      window.open(CHIA_COLLECTION_URL, '_blank');
    } else {
      onChainSelect(chainId);
      setMintingChain(chainId);
      setIsModalOpen(true);
    }
  };

  const isChainEnabled = (chainId: string) => {
    return CHAIN_AVAILABILITY[chainId as keyof typeof CHAIN_AVAILABILITY];
  };

  const getMintButtonText = (chainId: string) => {
    if (!isPastMintDate) return 'Minting Soon';
    if (!isChainEnabled(chainId)) return 'Not Available';
    return 'Mint Now';
  };

  const toggleDescription = (chainId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedDescriptions(prev => ({
      ...prev,
      [chainId]: !prev[chainId]
    }));
  };

  return (
    <section id="mint-section" className="py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center max-w-4xl mx-auto mb-12"
      >
        <h2 className="text-4xl font-bold mb-6 text-[#9333EA]">Choose Your Chain</h2>
        <p className="text-sm md:text-base text-nft-muted mb-8">The Cryptfluencers are taking their satirical shenanigans multi-chain, minting on three different blockchains: Chia, Pulsechain, and Base. Each of these chains offers a unique flavor of crypto chaos, making them the perfect playground for our pixelated legends.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 max-w-6xl mx-auto px-4"
      >
        {chainDetails.map((chain, index) => (
          <motion.div
            key={chain.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
            className="h-full"
          >
            <Card
              className={`chain-card h-full flex flex-col bg-black/80 backdrop-blur-xl border relative ${
                selectedChain === chain.id ? "ring-2 ring-nft-accent shadow-lg shadow-nft-accent/20 border-nft-accent" : "border-white/10 hover:border-white/20"
              }`}
            >
              <div className="space-y-4 flex flex-col h-full p-6 relative z-10">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-nft-accent/20 to-blue-500/20 rounded-full blur-xl" />
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-nft-accent to-blue-500 p-0.5 flex items-center justify-center mx-auto mb-4 relative">
                    <div className="h-full w-full rounded-full bg-black/90 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                      <img
                        src={chain.logo}
                        alt={`${chain.name} logo`}
                        className="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3 flex-grow">
                  <h3 className="text-xl font-bold text-[#9333EA] text-center">{chain.name}</h3>
                  <p className="text-lg md:text-xl text-[#9333EA] font-mono font-bold text-center">{chain.price}</p>
                  <div className="space-y-2 text-sm mt-4">
                    <div className="flex justify-between text-nft-muted">
                      <span>Supply:</span>
                      <span className="text-white">{chain.supply}</span>
                    </div>
                    <div className="flex justify-between text-nft-muted">
                      <span>TPS:</span>
                      <span className="text-white">{chain.tps}</span>
                    </div>
                    <div className="flex justify-between text-nft-muted">
                      <span>Finality:</span>
                      <span className="text-white">{chain.finality}</span>
                    </div>
                    
                    <div className="mt-4">
                      <div className="relative">
                        <p className={`text-xs mt-3 text-nft-muted leading-relaxed ${!expandedDescriptions[chain.id] ? 'line-clamp-2' : ''}`}>
                          {chain.description}
                        </p>
                        <button
                          onClick={(e) => toggleDescription(chain.id, e)}
                          className="text-xs text-[#9333EA] hover:text-[#9333EA]/80 transition-colors mt-1 cursor-pointer"
                        >
                          {expandedDescriptions[chain.id] ? 'Read Less' : 'Read More'}
                        </button>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-nft-muted">
                          {chain.id === 'chia' ? 'Collection ID:' : 'Contract Address:'}
                        </p>
                        <p className="text-xs font-mono text-[#9333EA] break-all mt-1">
                          {chain.id === 'chia' ? chain.collectionId : CONTRACT_ADDRESSES[chain.id as keyof typeof CONTRACT_ADDRESSES]}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => handleChainSelect(chain.id)}
                  className={`w-full mt-6 transition-all duration-300 relative z-20 bg-[#9333EA] hover:bg-[#9333EA]/80 text-white ${
                    isPastMintDate && isChainEnabled(chain.id) ? 'active:scale-95' : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!isPastMintDate || !isChainEnabled(chain.id)}
                >
                  {getMintButtonText(chain.id)}
                </Button>

                <a
                  href={chain.walletLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center text-sm text-[#9333EA] hover:text-[#9333EA]/80 transition-colors mt-2"
                >
                  Download {chain.walletName} →
                </a>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {mintingChain && mintingChain !== 'chia' && (
        <MintModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          selectedChain={mintingChain}
        />
      )}
    </section>
  );
}